import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
  Chip,
  IconButton,
  Typography
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

const FIELD_TYPES = [
  'text',
  'number',
  'phone',
  'email',
  'dropdown',
  'yesno',
  'multientry',
  'image',
  'textarea'
];

const FieldEditor = ({ open, field, onClose, onSave }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    key: '',
    type: 'text',
    label: {
      en: '',
      mr: ''
    },
    required: false,
    voiceEnabled: false,
    options: []
  });

  const [newOption, setNewOption] = useState({ en: '', mr: '' });

  useEffect(() => {
    if (field) {
      setFormData(field);
    } else {
      setFormData({
        key: '',
        type: 'text',
        label: {
          en: '',
          mr: ''
        },
        required: false,
        voiceEnabled: false,
        options: []
      });
    }
  }, [field, open]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name.startsWith('label.')) {
      const lang = name.split('.')[1];
      setFormData({
        ...formData,
        label: {
          ...formData.label,
          [lang]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === 'required' || name === 'voiceEnabled' ? checked : value
      });
    }
  };

  const handleAddOption = () => {
    if (newOption.en) {
      setFormData({
        ...formData,
        options: [...(formData.options || []), newOption]
      });
      setNewOption({ en: '', mr: '' });
    }
  };

  const handleRemoveOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Generate key from English label if not set
    if (!formData.key && formData.label.en) {
      formData.key = formData.label.en
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    }

    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('forms.fieldSettings')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Field Key (unique identifier)"
              name="key"
              value={formData.key}
              onChange={handleChange}
              helperText="Auto-generated from English label if left blank"
            />

            <FormControl fullWidth>
              <InputLabel>{t('forms.fieldType')}</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                label={t('forms.fieldType')}
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`fieldTypes.${type}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={`${t('forms.fieldLabel')} (English)`}
              name="label.en"
              value={formData.label.en}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label={`${t('forms.fieldLabel')} (मराठी)`}
              name="label.mr"
              value={formData.label.mr}
              onChange={handleChange}
            />

            {formData.type === 'dropdown' && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Dropdown Options
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    label="Option (English)"
                    value={newOption.en}
                    onChange={(e) => setNewOption({ ...newOption, en: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                  />
                  <TextField
                    size="small"
                    label="Option (मराठी)"
                    value={newOption.mr}
                    onChange={(e) => setNewOption({ ...newOption, mr: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                  />
                  <IconButton onClick={handleAddOption} color="primary">
                    <AddIcon />
                  </IconButton>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {formData.options?.map((option, index) => (
                    <Chip
                      key={index}
                      label={`${option.en} / ${option.mr || 'N/A'}`}
                      onDelete={() => handleRemoveOption(index)}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    name="required"
                    checked={formData.required}
                    onChange={handleChange}
                  />
                }
                label={t('forms.required')}
              />

              <FormControlLabel
                control={
                  <Switch
                    name="voiceEnabled"
                    checked={formData.voiceEnabled}
                    onChange={handleChange}
                  />
                }
                label={t('forms.voiceEnabled')}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FieldEditor;
